/**
 * Login Intent Tracking System
 *
 * This module tracks when a user initiates a login action versus when
 * they're just reloading the page. This is crucial for determining whether
 * to preserve conversation history (during login) or clear it (during reload).
 */

const LOGIN_INTENT_KEY = 'et_ai_login_intent'
const LOGIN_INTENT_EXPIRY = 5 * 60 * 1000 // 5 minutes

/**
 * Set login intent when user initiates a login action
 * This should be called when user clicks login/register buttons
 */
export const setLoginIntent = () => {
  const intent = {
    timestamp: Date.now(),
    action: 'user_initiated_login',
  }
  sessionStorage.setItem(LOGIN_INTENT_KEY, JSON.stringify(intent))
}

/**
 * Check if there's a valid login intent
 * @returns {boolean} True if user initiated login recently, false otherwise
 */
export const hasLoginIntent = () => {
  try {
    const intentStr = sessionStorage.getItem(LOGIN_INTENT_KEY)

    if (!intentStr) {
      return false
    }

    const intent = JSON.parse(intentStr)
    const now = Date.now()
    const ageMs = now - intent.timestamp
    const isValid = ageMs < LOGIN_INTENT_EXPIRY

    return isValid
  } catch (error) {
    return false
  }
}

/**
 * Clear login intent after it's been consumed
 */
export const clearLoginIntent = () => {
  sessionStorage.removeItem(LOGIN_INTENT_KEY)
}

/**
 * Check if this is a user-initiated login (not a page reload)
 * NOTE: This does NOT consume the intent - call clearLoginIntent() separately
 * @returns {boolean} True if user initiated login, false if page reload
 */
export const isUserInitiatedLogin = () => hasLoginIntent()
